import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { List, ListItemProps, Popper, Spinner, StatusDot } from '@xsolla/ui-core';
import { CogDouble } from '@xsolla/ui-icons';

import {
  connectSocialConnectionsList,
  disconnectSocialConnectionsList,
  SocialConnectionsResponse
} from '../../../api';
import { ChosenSocials } from './index';
import { CardSpinner } from './SocialNetworkCard/style';
import { PillActionText, PillContentWrapper, StyledPill, StyledSeparator } from './style';

type Props = {
  socialsList: ChosenSocials | null;
  projectId: string;
  setSocials: React.Dispatch<React.SetStateAction<SocialConnectionsResponse | null>>;
  setFilteredSocials: React.Dispatch<React.SetStateAction<SocialConnectionsResponse | null>>;
  setChosenSocials: React.Dispatch<React.SetStateAction<ChosenSocials | null>>;
};

export const SelectConnectionMode = ({
  projectId,
  socialsList,
  setSocials,
  setFilteredSocials,
  setChosenSocials
}: Props): JSX.Element => {
  const intl = useIntl();
  const [fetching, setFetching] = useState<boolean>(false);

  const chosenSocials = useMemo(() => {
    return (
      socialsList &&
      (Object.values(socialsList).filter((item) => !!item) as SocialConnectionsResponse)
    );
  }, [socialsList]);

  const setSocialMode = useCallback(
    (isConnect?: boolean) => {
      setSocials(
        (prev) =>
          prev &&
          prev.map((item) =>
            socialsList?.[item.provider] ? { ...item, connected: !!isConnect } : item
          )
      );
      setFilteredSocials(
        (prev) =>
          prev &&
          prev.map((item) =>
            socialsList?.[item.provider] ? { ...item, connected: !!isConnect } : item
          )
      );
      setChosenSocials((prev) => {
        if (!prev) {
          return prev;
        }
        return Object.entries(prev).reduce(
          (acc, [key, value]) =>
            value || !!isConnect ? { ...acc, [key]: { ...value, connected: !!isConnect } } : null,
          {}
        );
      });
    },
    [socialsList]
  );

  const connectHandle = async () => {
    if (!chosenSocials) {
      return;
    }

    setFetching(true);
    await connectSocialConnectionsList(
      projectId,
      chosenSocials.filter((social) => !social.connected && !social.is_custom)
    );
    setSocialMode(true);
    setFetching(false);
  };

  const disconnectHandle = async () => {
    if (!chosenSocials) {
      return;
    }
    setFetching(true);
    await disconnectSocialConnectionsList(
      projectId,
      chosenSocials.filter((social) => social.connected && !social.is_custom)
    );
    setSocialMode();
    setFetching(false);
  };

  const SocialModeOptions: ListItemProps[] = useMemo(() => {
    const hasDisconnectedSocial = chosenSocials?.find((item) => !item?.connected);
    const hasConnectedSocial = chosenSocials?.find((item) => item?.connected);

    return [
      {
        children: intl.messages['social-connection.mode.disconnect'],
        onClick: () => disconnectHandle(),
        icon: () => <StatusDot palette="muted" />,
        iconAlign: 'right',
        'data-test-id': 'socials-disconnect-selected',
        disabled: !hasConnectedSocial
      },
      {
        children: intl.messages['social-connection.mode.connect'],
        onClick: () => connectHandle(),
        icon: () => <StatusDot palette="action" />,
        iconAlign: 'right',
        'data-test-id': 'socials-connect-selected',
        disabled: !hasDisconnectedSocial
      }
    ];
  }, [chosenSocials]);

  return (
    <Popper entry={<List items={SocialModeOptions} />}>
      {(referenceProps) => (
        <StyledPill {...referenceProps} palette="brand" data-test-id="socials-manage-btn">
          <PillContentWrapper>
            {fetching ? (
              <CardSpinner>
                <Spinner size={16} />
              </CardSpinner>
            ) : (
              <CogDouble size={24} />
            )}
            <PillActionText>{intl.messages['social-connection.mode.title']}</PillActionText>
            <StyledSeparator height="14px" />
          </PillContentWrapper>
        </StyledPill>
      )}
    </Popper>
  );
};
